import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import ModuleCard from './ModuleCard';
import { IModule } from 'interface/course';
import { makeStyles, Menu, MenuItem, Typography } from '@material-ui/core';
import { useAppSelector } from 'hooks/redux';
import Skeleton from '@material-ui/lab/Skeleton';
import { MenuContext } from './menuContext';
import { IDownload } from 'interface/filedownload';
import Download from '@eduzz/houston-icons/Download';
import Exit from '@eduzz/houston-icons/Exit';
import { API_V4_BASE_URL } from 'settings';
import { useHistory, useParams } from 'react-router';
import { isStatusNegative } from 'utils';
import { fetchLessonFiles } from 'services/lesson';
import { clearModules, getModules } from 'redux/modules/slice';

interface IProps {
  onCloseMobile: Function;
}

const useStyles = makeStyles((theme) => ({
  modules: {
    height: 'calc(100% - 150px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    background: theme.palette.safevideo.dark,
    flex: '1',
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.grey[300],
      borderRadius: 8,
    },
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.grey[600],
      borderRadius: 8,
    },
  },
  modulesSkeleton: {
    height: 56,
    background: theme.palette.safevideo.dark,
    border: `1px solid ${theme.palette.grey['900']}`,
  },
  filesSkeleton: {
    position: 'relative',
    height: 28,
    margin: 6,
  },
  paperMenu: {
    position: 'relative',
    overflow: 'inherit',
    width: 238,
  },
  menuItem: {
    '& > li': {
      padding: '13px 16px',
    },
    '& > li > p': {
      display: '-webkit-box',
      '-webkit-box-orient': 'vertical',
      '-webkit-line-clamp': 1,
      overflow: 'hidden',
      wordBreak: 'break-all',
      whiteSpace: 'pre-wrap',
    },
    '& > li:nth-child(5n) > p': {
      fontWeight: 700,
    },
  },
  iconMenu: {
    marginRight: 10,
  },
}));
const ModuleList = (props: IProps) => {
  const classes = useStyles();
  const lesson = useAppSelector((state) => state.newLesson.lesson);
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [files, setFiles] = useState<IDownload[]>([]);
  const [shouldScrollAnotherLesson, setShouldScrollAnotherLesson] = useState(false);
  const isLessonLocked = isStatusNegative(lesson.status);
  const modules = useAppSelector((state) => state.newModules.modules);
  const dispatch = useDispatch();
  const { courseHash } = useParams<{ courseHash: string }>();

  useEffect(() => {
    if (!shouldScrollAnotherLesson) {
      return;
    }
    scrollToElement();
    setShouldScrollAnotherLesson(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson.id]);

  const scrollToElement = () => {
    const timeToCloseMenu = 500;
    setTimeout(() => {
      const el = document.getElementById('files');
      el && el.scrollIntoView({ behavior: 'smooth' });
    }, timeToCloseMenu);
  };

  const handleOpenMenu = async (event: React.MouseEvent<HTMLButtonElement>, id: any) => {
    if (!!isLessonLocked) return;
    setAnchorEl(event.currentTarget);
    const responseFiles = await fetchLessonFiles(id);
    setFiles(responseFiles);
  };

  const handleClose = () => {
    setFiles([]);
    setAnchorEl(null);
  };

  const downloadFile = (link: string) => {
    window.open(`${API_V4_BASE_URL}${link}`);
  };

  const seeAllFiles = async () => {
    setAnchorEl(null);
    setFiles([]);
    props.onCloseMobile();

    if (lesson.id !== files[0].id_lesson) {
      if (!!isLessonLocked) return;
      history.replace(files[0].id_lesson.toString());
      setShouldScrollAnotherLesson(true);
      return;
    }
    scrollToElement();
  };

  useEffect(() => {
      dispatch(getModules(courseHash));
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearModules());
    };
  }, [dispatch]);

  if (!modules) {
    return (
      <div className={classes.modules}>
        {Array.from(Array(8).keys()).map((_, index) => (
          <Skeleton className={classes.modulesSkeleton} key={index} variant='rect' animation='wave' />
        ))}
      </div>
    );
  }

  return (
    <div className={classes.modules}>
      <MenuContext.Provider value={{ handleOpenMenu }}>
        <div aria-label='Lista de módulos do curso'>
          {modules.map((mod: IModule, key: number) => {
            return (
              <ModuleCard
                module={mod}
                key={key}
                index={key}
                title={mod.title}
                lessons={mod.lessons}
                curLesson={lesson}
              />
            );
          })}
        </div>
      </MenuContext.Provider>
      <Menu
        aria-label='Menu para visualizar arquivos da aula'
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        classes={{
          paper: classes.paperMenu,
          list: classes.menuItem,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {(files ?? []).length
          ? (files ?? []).map((f, index) => {
              return (
                <MenuItem id='sidemenu-lesson-files-menu' key={index} onClick={() => downloadFile(f.file_name)}>
                  <Download className={classes.iconMenu} />
                  <Typography aria-label='Título dos arquivos' variant='body2'>
                    {f.title}
                  </Typography>
                </MenuItem>
              );
            })
          : Array.from(Array(4).keys()).map((_, index) => (
              <Skeleton className={classes.filesSkeleton} key={index} variant='rect' animation='wave' />
            ))}
        {(files ?? []).length > 3 && (
          <MenuItem button={true} aria-label='Botão visualizar todos os arquivos da aula' onClick={seeAllFiles}>
            <Exit className={classes.iconMenu} />{' '}
            <Typography variant={'body1'} id='lesson-files-see-all'>
              Ver todos
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export default ModuleList;
